import { ClearanceCategoryNode } from "@SupplyLand/Services/SupplyLandService";

export const CLEARANCE = "clearance";

export interface CategoryMaps {
    nodeById: Map<string, ClearanceCategoryNode>;
    parentById: Map<string, string>;
}

/** Returns true when the supplied category context is the top-level "Clearance" category. */
export const isClearanceCategory = (category?: { name?: string; shortDescription?: string }): boolean =>
    (category?.name ?? "").trim().toLowerCase() === CLEARANCE ||
    (category?.shortDescription ?? "").trim().toLowerCase() === CLEARANCE;

/** Flattens the tree into lookups of node-by-id and parent-id-by-id for fast traversal. */
export const buildCategoryMaps = (
    nodes: ClearanceCategoryNode[],
    parentId: string | undefined,
    maps: CategoryMaps,
): void => {
    nodes.forEach(node => {
        maps.nodeById.set(node.id, node);
        if (parentId) {
            maps.parentById.set(node.id, parentId);
        }
        buildCategoryMaps(node.subCategories ?? [], node.id, maps);
    });
};

export const buildMaps = (nodes: ClearanceCategoryNode[]): CategoryMaps => {
    const maps: CategoryMaps = { nodeById: new Map(), parentById: new Map() };
    buildCategoryMaps(nodes, undefined, maps);
    return maps;
};

export const collectSelfAndDescendants = (node: ClearanceCategoryNode): string[] => [
    node.id,
    ...(node.subCategories ?? []).flatMap(collectSelfAndDescendants),
];

/**
 * Total product count for a node's whole branch (the node plus every descendant). Used so a parent
 * category's filter count reflects the sum of its sub-categories rather than its own direct count.
 */
export const sumBranchProductCount = (node: ClearanceCategoryNode): number =>
    node.productCount + (node.subCategories ?? []).reduce((total, child) => total + sumBranchProductCount(child), 0);

export const branchHasSelection = (node: ClearanceCategoryNode, selected: Set<string>): boolean =>
    selected.has(node.id) || (node.subCategories ?? []).some(child => branchHasSelection(child, selected));

/** Collects the de-duplicated product ids for every selected category. */
export const computeProductIds = (selected: Set<string>, maps: CategoryMaps): string[] => {
    const productIdSet = new Set<string>();
    selected.forEach(id => {
        maps.nodeById.get(id)?.productIds?.forEach(productId => productIdSet.add(productId));
    });
    return Array.from(productIdSet);
};

/**
 * Rebuilds the selected-category set from the product ids currently applied to the product list. A
 * category counts as selected when every one of its product ids is present in the applied set.
 */
export const deriveSelectedCategoryIds = (maps: CategoryMaps, appliedProductIds?: string[]): Set<string> => {
    const selected = new Set<string>();
    if (appliedProductIds?.length) {
        const appliedSet = new Set(appliedProductIds);
        maps.nodeById.forEach(node => {
            if (node.productIds.length && node.productIds.every(id => appliedSet.has(id))) {
                selected.add(node.id);
            }
        });
    }
    return selected;
};

/**
 * Removes a category and its whole branch (descendants) from the selection, then walks up the tree
 * de-selecting any ancestor that can no longer be fully selected.
 */
export const removeBranchFromSelection = (
    node: ClearanceCategoryNode,
    selected: Set<string>,
    maps: CategoryMaps,
): Set<string> => {
    const next = new Set(selected);
    collectSelfAndDescendants(node).forEach(id => next.delete(id));
    let parentId = maps.parentById.get(node.id);
    while (parentId) {
        next.delete(parentId);
        parentId = maps.parentById.get(parentId);
    }
    return next;
};

/** Returns the selected nodes whose parent is not itself selected (the cleanest tags to display). */
export const getTopMostSelectedNodes = (selected: Set<string>, maps: CategoryMaps): ClearanceCategoryNode[] => {
    const result: ClearanceCategoryNode[] = [];
    selected.forEach(id => {
        const parentId = maps.parentById.get(id);
        if (!parentId || !selected.has(parentId)) {
            const node = maps.nodeById.get(id);
            if (node) {
                result.push(node);
            }
        }
    });
    return result.sort((a, b) => a.sortOrder - b.sortOrder);
};
